import { IconType } from 'react-icons'
import {
  FiActivity,
  FiArchive,
  FiBarChart2,
  FiBell,
  FiBriefcase,
  FiCheckSquare,
  FiClock,
  FiCreditCard,
  FiDownloadCloud,
  FiFileText,
  FiFolder,
  FiGlobe,
  FiGrid,
  FiHome,
  FiKey,
  FiLayers,
  FiLayout,
  FiLink2,
  FiPackage,
  FiPieChart,
  FiSettings,
  FiShield,
  FiShoppingBag,
  FiSliders,
  FiUser,
  FiUserCheck,
  FiUserPlus,
  FiUsers,
} from 'react-icons/fi'

/**
 * Menu icons are resolved locally instead of from the backend's `iconClass`.
 * The API values were inconsistent (missing on most modules, and a mix of icon
 * sets where present), so the sidebar hardcodes one coherent icon family here.
 *
 * First matching rule wins, so the list runs specific -> generic. The haystack
 * is `"<route> <title>"` lowercased, which means a rule can key off either the
 * route alias (stable) or the backend-configured display name (translatable).
 */
const ICON_RULES: Array<{ match: RegExp; icon: IconType }> = [
  // --- Reporting / analytics ---
  { match: /superset|analytic|insight|chart/, icon: FiPieChart },
  { match: /report|summary|statistic/, icon: FiBarChart2 },

  // --- Customer onboarding ---
  { match: /corporate/, icon: FiBriefcase },
  { match: /new[\s-]?cif/, icon: FiUserPlus },
  { match: /existing[\s-]?cif/, icon: FiUserCheck },
  { match: /kyc|compliance/, icon: FiShield },
  { match: /screening|sanction|aml/, icon: FiSliders },
  { match: /linked[\s-]?entit|relation/, icon: FiLink2 },
  { match: /preliminary|unverified|pending/, icon: FiClock },

  // --- Accounts ---
  { match: /closure|closed/, icon: FiArchive },
  { match: /status|tracking/, icon: FiActivity },
  { match: /external[\s-]?source/, icon: FiDownloadCloud },
  { match: /retail|aloi/, icon: FiShoppingBag },
  { match: /online|internet|instant|quick/, icon: FiGlobe },
  { match: /ancillary|service/, icon: FiPackage },
  { match: /account/, icon: FiCreditCard },

  // --- Documents / forms ---
  { match: /document|dms|file[\s-]?manage/, icon: FiFolder },
  { match: /form/, icon: FiFileText },
  { match: /cms|content|page|media/, icon: FiLayout },

  // --- Administration ---
  { match: /oauth|client|token|credential/, icon: FiKey },
  { match: /user|role|permission/, icon: FiUsers },
  { match: /setting|configuration/, icon: FiSettings },
  { match: /popup|notification|alert/, icon: FiBell },

  // --- Workflow roles ---
  { match: /checker|approval|review/, icon: FiCheckSquare },
  { match: /branch|maker/, icon: FiHome },

  // --- Fixed entries ---
  { match: /dashboard|overview/, icon: FiGrid },
  { match: /profile|my[\s-]?account/, icon: FiUser },
]

/** Icon for a module that matched no rule. */
export const DEFAULT_MENU_ICON: IconType = FiLayers

export const getMenuIcon = (route?: string, title?: string): IconType => {
  const haystack = `${route || ''} ${title || ''}`.toLowerCase()
  return ICON_RULES.find((rule) => rule.match.test(haystack))?.icon || DEFAULT_MENU_ICON
}
